import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { Status } from '@bingads-webui-campaign-react/diagnostic-card';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { statusAndActionReport, auditPointDisplayed, getFullProductUrl } from './utils';
import { DetailCardsKey } from '../const';

const STATUS_TEXT = {
  Active: _TL_('Active'),
  Paused: _TL_('Paused'),
  UnderReview: _TL_('Under review'),
  BudgetPaused: _TL_('Budget paused'),
};

const getCardDetails = ({
  auditPoint, scope, i18n, activity, alertHelper, locale,
}) => {
  const {
    values: {
      cid,
      aid,
      campaignId,
    },
  } = scope;

  const onNavigateToBudgetEditSettingsPage = () => {
    statusAndActionReport({
      activity,
      type: 'campaign',
      action: 'navigate to settings page',
      details: `AuditPoint: ${auditPoint}`,
    });

    if (!isSDK()) {
      scope.save(getRoutePathById(allSiteMap, 'settings'));
    } else {
      const url = getRoutePathById(allSiteMap, 'sdk-budget-settings');
      scope.save(url);
    }
  };

  const onNavigateToAssetGroupsPage = () => {
    statusAndActionReport({
      activity,
      type: 'campaign',
      action: 'navigate to asset groups page',
      details: `AuditPoint: ${auditPoint}`,
    });

    scope.save(getRoutePathById(allSiteMap, 'appadsassetgroups'));
  };

  auditPointDisplayed({ activity, auditPoint, area: 'Campaign' });

  switch (auditPoint) {
    case 'CampaignStatusUserPaused':
      return {
        cardTitle: i18n.getString(_TL_('Reactivate your campaign')),
        cardMsg: i18n.getString(_TL_('A paused campaign cannot generate impressions or conversions.')),
        onCardButtonClicked: () => {
          statusAndActionReport({
            activity,
            type: 'campaign',
            action: 'unpause campaign',
            details: `AuditPoint: ${auditPoint}`,
          });

          edmCampaign
            .Customers.$withKey(cid)
            .Accounts.$withKey(aid)
            .Campaigns.$withKey(campaignId)
            .bulkUpdate({
              Id: campaignId,
              Status: 'Active',
            }).then(() => {
              alertHelper.showSuccess(i18n.getString(_TL_('Campaign reactivated.')));
            }, (e) => {
              activity.error(JSON.stringify(e));
              alertHelper.showError(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
            });
        },
        cardButtonText: i18n.getString(_TL_('Reactivate')),
      };

    case 'AllAssetGroupsEditorialPending':
      return {
        cardMsg: i18n.getString(_TL_('There are one or multiple asset groups in this campaign still under editorial review.')),
      };

    case 'AllAdGroupsEditorialPending':
      return {
        cardMsg: i18n.getString(_TL_('There are one or multiple ad groups in this campaign still under editorial review.')),
      };

    case 'CampaignStatusBudgetPaused':
    case 'CampaignStatusActiveButBudgetPaused':
      return {
        statusLabelMsg: i18n.getString(_TL_('Eligible (limited)')),
        status: Status.Warning,
        cardTitle: i18n.getString(_TL_('Campaign is eligible but limited by budget')),
        cardMsg: i18n.getString(_TL_('Your campaign ran out of budget recently, which caused your ads not being shown as often as they could be. Raising your budget can help make sure you don\'t lose out on potential customers. To resume your ads now, you can increase your budget.')),
        onCardButtonClicked: onNavigateToBudgetEditSettingsPage,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/53050/1`,
      };

    case 'CampaignStatusOutOfBudget':
    case 'CampaignStatusPausedAndOutOfBudget':
      return {
        statusLabelMsg: i18n.getString(_TL_('Paused by budget constraint')),
        cardTitle: i18n.getString(_TL_('Paused campaign due to budget constraints')),
        cardMsg: i18n.getString(_TL_('Because your campaign has run out of budget, we\'ve paused your ads.  Ad delivery  will resume at the start of the next day. To resume your ads now, you can increase your budget.')),
        onCardButtonClicked: onNavigateToBudgetEditSettingsPage,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/53050/1`,
      };

    case 'MissVideoAsset':
      return {
        statusLabelMsg: i18n.getString(_TL_('Missing Video')),
        cardTitle: i18n.getString(_TL_('Missing video assets')),
        cardMsg: i18n.getString(_TL_('Your campaign has no video assets. Add videos now to boost your campaign\'s reach and improve performance.')),
        onCardButtonClicked: onNavigateToAssetGroupsPage,
        cardButtonText: i18n.getString(_TL_('Review')),
      };

    case 'CampaignStatusActive':
      return {};

    default:
      activity.error(`Audit point mapping not found in getCampaignStatus:${auditPoint}`);
      return {};
  }
};

export const getCampaignStatus = ({
  details, scope, i18n, activity, locale, alertHelper,
}) => {
  if (_.isEmpty(details)
    || _.isEmpty(_.get(details, 'Value'))
    || _.get(details, ['Value', 'ErrorCode'], 0) !== 0
    || _.get(details, ['Value', 'ErrorMessage'])) {
    statusAndActionReport({
      activity,
      type: 'campaign',
      details: `Error - details: ${JSON.stringify(details)}`,
    });
    return {
      name: i18n.getString(_TL_('Campaign')),
      key: DetailCardsKey.campaign,
    };
  }

  const status = _.get(details, ['Value', 'Status']);
  const auditPoint = _.get(details, ['Value', 'AuditPoints', 0]);

  const cardDetails = getCardDetails({
    auditPoint, scope, i18n, activity, alertHelper, locale,
  });

  let statusIcon = Status.Error;

  if (status === 'Active') {
    statusIcon = Status.Active;
  } else if (status === 'UnderReview') {
    statusIcon = Status.Pending;
  }

  return {
    name: i18n.getString(_TL_('Campaign')),
    key: DetailCardsKey.campaign,

    status: statusIcon,
    statusLabelMsg: i18n.getString(_.get(STATUS_TEXT, status, _TL_(''))),

    ...cardDetails,
  };
};
